import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import ScreenerFilters from "../components/screener/ScreenerFilters";
import ScreenerTable from "../components/screener/ScreenerTable";

/**
 * Stock Screener page.
 *
 * Thin orchestrator — owns the filter/sort state and data fetching. Filter
 * UI lives in `ScreenerFilters` and the results table + client-side sort in
 * `ScreenerTable`. Was 386 lines as a single file; now 95.
 */
export default function Screener() {
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState({
    sector: "",
    market_cap: "",
    pe: "",
    performance: "",
    dividend: "",
    country: "",
    avg_volume: "",
  });
  const [sortBy, setSortBy] = useState("marketcap_desc");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cached, setCached] = useState(false);

  useEffect(() => {
    api.get("/screener/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const buildQuery = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    params.set("sort_by", sortBy);
    params.set("limit", "50");
    return params.toString();
  }, [filters, sortBy]);

  const runScreen = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/screener?${buildQuery}`);
      setItems(r.data.items || []);
      setCached(!!r.data.cached);
      if (r.data.error) setError(r.data.error);
    } catch (e) {
      console.warn("screener run failed:", e);
      setError("Failed to run screener");
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  // Auto-run with default filters on first load. Subsequent runs are
  // user-initiated via the Apply button so we don't spam the Finviz cache.
  useEffect(() => {
    runScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8 md:py-12" data-testid="screener-page">
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Stock Screener
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B]">
            Find your next stock
          </h1>
          <p className="text-[#6B7068] mt-2 text-sm leading-relaxed max-w-xl">
            Mix and match filters to discover stocks across every sector. Powered by Finviz —
            results cached for 30 minutes.
          </p>
        </header>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6 lg:gap-8">
          <ScreenerFilters
            filters={filters}
            setFilters={setFilters}
            sortBy={sortBy}
            setSortBy={setSortBy}
            options={options}
            loading={loading}
            runScreen={runScreen}
          />
          <ScreenerTable
            items={items}
            loading={loading}
            error={error}
            cached={cached}
          />
        </div>
      </main>
    </div>
  );
}
